import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';

interface ILandRenewRevenueReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
}

// Matches ClientsBilledReportDialog.tsx's own label/field convention (the gold standard for these
// report-parameter dialogs): explicit left-aligned nowrap labels with a fixed width/marginRight -
// marginRight is explicit rather than left to RHFInputBox's own default because RHFSelect's
// labelOnLeft path (used by Report Type below) doesn't add one on its own, which would otherwise
// leave its <select> flush against its label while the other three rows get a gap - and fields
// narrowed to 65% instead of stretched to the full row width.
const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem', textAlign: 'left', marginRight: '1rem' };
const fieldStyle: React.CSSProperties = { width: '65%' };

// Labels/order confirmed directly off landrenew_revenue.fmt's own TYPE poplist (FRM50_IDFO/F50T
// records at TP=28/29/30) - the display label and the value sent to the proc are genuinely
// different strings, not a value-as-its-own-label list like Office/Status elsewhere.
const TYPE_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'STD', label: 'WIP Not Applied' },
    { value: 'WIP', label: 'WIP Applied' },
    { value: 'WIPONLY', label: 'WIP Only' },
];

// Backs the Accounts Receivable "Land/Renewables Revenue" report - deployed on the report server as
// LandRenewRevenue (see ReportService.GetLandRenewRevenueReportFile for the confirmed
// BegDate|EndDate|Wip @Parameters shape - read directly off dbo.usp_LandRenewRevenue_Summary, which
// treats @Wip as one of STD/WIP/WIPONLY, not a free-text value). Unlike State on the Invoice
// Breakdown By County dialog, Wip is required - the proc's three UNION branches each gate on an
// exact @Wip match, so a blank/unrecognized value returns nothing. Filename mirrors Oracle's own
// FILENAME field - purely a client-side download name, not sent to the report server.
const LandRenewRevenueReportDialog = (props: ILandRenewRevenueReportDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { get: getReport, response: reportResponse } = useFetch();
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');

    const generateReport = async () => {
        const values = getValues();
        if (!values.begDate || !values.endDate) {
            setMessage('A beginning and ending date must be entered.');
            return;
        }

        setMessage('');
        setIsWorking(true);
        const params = new URLSearchParams({ begDate: values.begDate, endDate: values.endDate, wip: values.wip || 'STD' });
        await getReport(`/v1.0/reports/GetReport/LandRenewRevenue?${params.toString()}`);
        setIsWorking(false);

        if (!reportResponse.ok) {
            setMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (reportResponse.status === 204) {
            setMessage('No revenue matched those filters.');
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        const baseName = (values.filename || 'LandRenewRevenue').trim().replace(/\.xlsx$/i, '');
        a.download = `${baseName || 'LandRenewRevenue'}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        props.hideDialog();
    };

    return (
        <Dialog title="Land/Renewables Revenue" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '25%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <RHFInputBox
                        label="Beginning Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="begDate"
                        type="date"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Ending Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="endDate"
                        type="date"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Report Type"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="wip"
                        defaultVal="STD"
                        options={TYPE_OPTIONS}
                        register={register}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Filename"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="filename"
                        type="text"
                        defaultVal="LandRenewRevenue"
                        register={register}
                        errors={errors}
                        style={fieldStyle}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={isWorking ? 'Generating...' : 'Generate'}
                    loading={isWorking}
                    onClick={() => generateReport()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text="Cancel"
                    onClick={() => props.hideDialog()}
                />
            </div>
            {!!message && (
                <div style={{ color: 'red', textAlign: 'center', marginTop: '0.5rem' }}>{message}</div>
            )}
        </Dialog>
    );
};

export default LandRenewRevenueReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/LandRenewRevenueReportDialog.tsx